/**
 * Parser SSE incremental + utilidades do fio dual do Modo Agente.
 *
 * Contratos: `.specify/project-ia-claude-code/spec-002-chat-agentico-estilo-
 * claude-code/contracts/events.json` (vocabulário, par dual) e
 * `contracts/internal-api.md` §5 (frames `id: <id_run>:<seq>`,
 * `frame_index`/`frame_count` quando o dual gera mais de um frame).
 *
 * Extraído de `apiChat.js` (T041) por duas razões:
 * 1. O parser anterior guardava o evento SSE em construção (`event:`/`data:`)
 *    em variáveis LOCAIS de `_processStreamLines`, que reiniciavam a cada
 *    chamada — um `read()` de rede que entrega `event: token\n` num chunk e
 *    `data: {...}\n\n` no próximo perdia o evento inteiro (o `event:` já
 *    tinha sumido quando o `data:` chegou, `apiChat.js:384` antes desta
 *    task). Aqui o estado do evento em construção é de INSTÂNCIA da classe
 *    `SseLineParser`: sobrevive entre chamadas de `consumeLine`, então a
 *    fronteira de bytes do TCP nunca corta um evento.
 * 2. `apiChat.js` já estava acima do teto de 500 linhas do CLAUDE.md antes
 *    desta task — mover o parser de baixo nível para cá é o que permite as
 *    adições da wave 5 sem estourar o arquivo principal.
 */

/**
 * Parser SSE incremental, linha a linha.
 *
 * Uso: alimente cada linha JÁ SEM o `\n` terminador via `consumeLine`. Uma
 * linha vazia fecha o evento em construção; `consumeLine` devolve `null`
 * enquanto o evento continua incompleto e `{event, data, id}` quando fecha.
 */
export class SseLineParser {
  // ⚠️ **Campo de classe NÃO compila neste repo** — o estado inicial vive no
  // `constructor`. O Babel daqui é `['@vue/app', '@babel/preset-env']` na
  // versão instalada, sem `@babel/plugin-proposal-class-properties`: a mesma
  // razão pela qual `?.`/`??` são proibidos (CLAUDE.md do repo). Com campo de
  // classe o webpack para no parse — *"Module parse failed: Unexpected token
  // (31:9)"* — e a aplicação INTEIRA deixa de compilar, não só este arquivo.
  //
  // O Jest não pega isso: `babel-jest` roda outra configuração e transforma o
  // campo normalmente. Foi assim que a conversão entrou verde pelo teste e
  // derrubou o dev server (medido em 18/09/2026, ao subir a tela).
  constructor() {
    this._event = ''
    this._dataLines = []
    this._id = null
  }

  /**
   * @param {string} rawLine Linha do fio, sem o `\n` terminador (pode ter `\r`).
   * @returns {{event: string, data: string, id: string|null}|null}
   */
  consumeLine(rawLine) {
    const line = rawLine.endsWith('\r')
      ? rawLine.substring(0, rawLine.length - 1)
      : rawLine
    if (line === '') {
      return this._flush()
    }
    // Comentário/keep-alive cru do protocolo SSE (`: ping` etc.) — sem efeito.
    if (line.startsWith(':')) {
      return null
    }
    const sepIdx = line.indexOf(':')
    const field = sepIdx === -1 ? line : line.substring(0, sepIdx)
    let value = sepIdx === -1 ? '' : line.substring(sepIdx + 1)
    if (value.startsWith(' ')) value = value.substring(1)
    if (field === 'event') {
      this._event = value
    } else if (field === 'data') {
      this._dataLines.push(value)
    } else if (field === 'id') {
      this._id = value
    }
    // `retry:` e campos desconhecidos: ignorados de propósito (fora do
    // vocabulário que o front consome).
    return null
  }

  _flush() {
    const hasData = this._dataLines.length > 0
    const result = (this._event && hasData)
      ? { event: this._event, data: this._dataLines.join('\n'), id: this._id }
      : null
    this._event = ''
    this._dataLines = []
    this._id = null
    return result
  }
}

/**
 * Extrai as linhas COMPLETAS (terminadas por `\n`) de um buffer bruto.
 * O `remainder` volta para o próximo chunk — uma fronteira de bytes no meio
 * de uma linha nunca produz linha quebrada.
 *
 * @param {string} buffer
 * @returns {{lines: string[], remainder: string}}
 */
export function extrairLinhasCompletas(buffer) {
  const lines = []
  let remaining = buffer
  let idx = remaining.indexOf('\n')
  while (idx !== -1) {
    lines.push(remaining.substring(0, idx))
    remaining = remaining.substring(idx + 1)
    idx = remaining.indexOf('\n')
  }
  return { lines: lines, remainder: remaining }
}

/**
 * Normaliza o JSON de um `data:` SSE nos dois formatos que o módulo trafega:
 *
 * - Modo Chat (legado, `POST /chat`): `data:` já É o payload do evento —
 *   sem envelope, sem `seq`.
 * - Modo Agente (spec-002, `GET /runs/{id}/eventos`): `data:` é o ENVELOPE
 *   `{event_type, id_escritorio, id_run, seq, timestamp, data, id_conversa?,
 *   actor?, frame_index?, frame_count?}` (`contracts/internal-api.md` §5) —
 *   o payload real do evento mora em `envelope.data`.
 *
 * `id_conversa` do envelope entra no payload quando o payload não o traz por
 * si (o `end` já carrega o seu; `error`/`start` do modo agente não).
 *
 * @param {*} parsedRaw JSON já parseado da linha `data:`.
 * @returns {{payload: Object, seq: (number|null), idRun: (string|null),
 *   frameIndex: (number|null), frameCount: (number|null)}}
 */
export function normalizarEnvelopeDeEvento(parsedRaw) {
  const ehEnvelopeDeRun = Boolean(
    parsedRaw && typeof parsedRaw === 'object' &&
    typeof parsedRaw.event_type === 'string' &&
    typeof parsedRaw.seq === 'number' &&
    parsedRaw.data && typeof parsedRaw.data === 'object'
  )
  if (!ehEnvelopeDeRun) {
    return { payload: parsedRaw, seq: null, idRun: null, frameIndex: null, frameCount: null }
  }
  const payload = { ...parsedRaw.data }
  if (parsedRaw.id_conversa !== undefined && payload.id_conversa === undefined) {
    payload.id_conversa = parsedRaw.id_conversa
  }
  return {
    payload: payload,
    seq: parsedRaw.seq,
    idRun: parsedRaw.id_run || null,
    frameIndex: typeof parsedRaw.frame_index === 'number' ? parsedRaw.frame_index : null,
    frameCount: typeof parsedRaw.frame_count === 'number' ? parsedRaw.frame_count : null,
  }
}

const MOLDE_UUID_V4 = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'

/**
 * UUID v4 para idempotência de cliente (`client_request_id`, `decision_id` —
 * `contracts/api.yaml`). `crypto.randomUUID` quando disponível (Node ≥ 14.17,
 * browsers modernos); fallback RFC4122 v4 sem `crypto` para ambientes sem a
 * API (não precisa ser criptograficamente forte — é só chave de dedupe).
 *
 * @returns {string}
 */
export function gerarUuidV4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  let saida = ''
  for (const c of MOLDE_UUID_V4) {
    if (c !== 'x' && c !== 'y') {
      saida += c
      continue
    }
    const r = Math.trunc(Math.random() * 16)
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    saida += v.toString(16)
  }
  return saida
}

/**
 * Backoff exponencial da reconexão por cursor ([adv C-30]): 1 s, 2 s, 4 s, ...
 * com teto (default 30 s, conforme a regra de `events.json`).
 *
 * @param {number} tentativa Zero-based.
 * @param {{tetoMs: number}=} opts
 * @returns {number} milissegundos
 */
export function calcularBackoffMs(tentativa, { tetoMs = 30000 } = {}) {
  return Math.min(1000 * Math.pow(2, tentativa), tetoMs)
}

/** Header comum das requisições `fetch` que abrem um stream SSE. */
export const STREAM_HEADER = { Accept: 'text/event-stream' }

/** `true` quando a resposta do `fetch` é um stream (`ReadableStream`) em vez
 * do envelope JSON comum — distingue os dois formatos que `iaCrmRequest`
 * pode devolver (ver `utils/api.js::parseResponseBody`). */
export function isStreamResponse(response) {
  return Boolean(response && response.body && response.body.getReader)
}
